import { FC, useContext } from 'react';
import { Button, Typography } from '@mui/material';
import { WebAssetOutlined as WebAssetOutlinedIcon } from '@mui/icons-material';
import { DataGridProProps, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';

import Column from '../../../../../components/Column';
import DataGridPro from '../../../../../components/CDGridPro';
import Row from '../../../../../components/Row';
import Utils from '../../../../../components/Utils';
import ApiService from '../../../../../ApiService';
import { UserContext } from '../../../../../App';

import { CampaignAdGroup, Platform } from '../../../../../types/Campaign';
import { useCampaignPageContext } from '../../../../../hooks/Contexts/Pages/CampaignPageContext';
import CampaignAdGroupStatus from '../../../../../features/Campaign/CampaignAdGroupStatus';
import CampaignMediaType from '../../../../../features/Campaign/CampaignMediaType';
import MediaTypeEditCell from '../../../../../features/Campaign/MediaTypeEditCell';

interface CampaignAdGroupAction {
    name: string;
    callback: (campaignAdGroup: CampaignAdGroup) => void;
}

type CampaignAdGroupDataGridProps = Omit<DataGridProProps, 'columns' | 'rows'> & {
    rows: CampaignAdGroup[];
    action: CampaignAdGroupAction;
};

const NoRowsOverlay: FC = () => {
    return (
        <Column gap={2} justifyContent="center" flex={1} height="100%">
            <Row justifyContent="center">
                <WebAssetOutlinedIcon sx={{ color: 'action.disabledBackground', fontSize: 80 }} />
            </Row>

            <Row justifyContent="center">
                <Typography variant="subtitle2" color="text.secondary">
                    Ad group(s) are not available
                </Typography>
            </Row>
        </Column>
    );
};

const CampaignAdGroupDataGrid: FC<CampaignAdGroupDataGridProps> = ({ rows, action, ...props }) => {
    const { campaign } = useCampaignPageContext();
    const { userContext } = useContext(UserContext);
    const isDraft = campaign.deliveryStatusId === 'DRAFT';
    const isPending = campaign.deliveryStatusId.startsWith('PENDING');
    const isAmazon = campaign.platform === Platform.AMAZON;
    let canEditMediaType = isAmazon ? isDraft || userContext.isSuperAdmin() : isPending || userContext.isAdmin();

    const columns: GridColDef[] = [
        {
            // filterable: false,
            flex: 1,
            field: 'name',
            headerName: 'Ad Group Name',
            display: 'flex',
        },
        {
            filterable: false,
            field: 'state',
            headerName: 'Status',
            renderCell: ({ row }: GridRenderCellParams) => {
                return <CampaignAdGroupStatus value={row.state} />;
            },
        },
        {
            flex: 1,
            filterable: false,
            field: 'mediaType',
            headerName: 'Media Type',
            renderCell: ({ row }: GridRenderCellParams) => {
                const value = row.mediaType || campaign.mediaType || 'Display';
                return canEditMediaType ? (
                    <MediaTypeEditCell
                        value={value}
                        onSave={(mediaType) => {
                            if (row.id) {
                                ApiService.updateAdGroupMediaType(row.id, mediaType);
                            }
                        }}
                    />
                ) : (
                    <CampaignMediaType value={value} />
                );
            },
        },
        {
            flex: 1,
            filterable: false,
            field: 'budgetAmount',
            headerName: 'DSP Budget',
            renderCell: ({ row }: GridRenderCellParams) => {
                return <>{row.budgetAmount ? Utils.formatValue(row.budgetAmount, 'dollar') : ' - '}</>;
            },
        },
        {
            flex: 1,
            filterable: false,
            field: 'startDateTime',
            headerName: 'Start Date',
            renderCell: ({ row }: GridRenderCellParams) => {
                return <>{row.startDateTime ? Utils.formatESTDate(row.startDateTime) : ' - '}</>;
            },
        },
        {
            flex: 1,
            filterable: false,
            field: 'endDateTime',
            headerName: 'End Date',
            renderCell: ({ row }: GridRenderCellParams) => {
                return <>{row.endDateTime ? Utils.formatESTDate(row.endDateTime) : ' - '}</>;
            },
        },
        {
            flex: 1,
            filterable: false,
            field: 'actions',
            headerName: 'Actions',
            headerAlign: 'right',
            align: 'right',
            renderCell: ({ row }: GridRenderCellParams) => {
                return (
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                            action.callback(row);
                        }}
                    >
                        {action.name}
                    </Button>
                );
            },
        },
    ];

    const getRowId = (row: CampaignAdGroup): string | number => {
        return row.id as number;
    };

    return (
        <DataGridPro
            // Overridable props
            {...props}
            disableColumnPinning
            getRowId={getRowId}
            columns={columns}
            rows={rows}
            slots={{
                toolbar: null,
                noRowsOverlay: NoRowsOverlay,
            }}
            sx={{
                '--DataGrid-overlayHeight': '212px',
            }}
        />
    );
};
export default CampaignAdGroupDataGrid;
